/**
 * Utility functions for job detail page
 */

/**
 * Determines if a hex color is dark (for text contrast)
 * @param hex - Hex color string (e.g., '#FF0000' or 'FF0000')
 * @returns true if color is dark, false otherwise
 */
export function isDark(hex: string): boolean {
  if (!hex) return false;
  let c = hex.replace('#', '');
  if (c.length === 3)
    c = c
      .split('')
      .map((x) => x + x)
      .join('');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // Perceived brightness
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

/**
 * Determines if a hex color is white or near-white
 * @param hex - Hex color string (e.g., '#FFFFFF' or 'FFFFFF')
 * @returns true if color is white or near-white, false otherwise
 */
export function isWhiteOrNearWhite(hex: string): boolean {
  if (!hex) return false;
  let c = hex.replace('#', '');
  if (c.length === 3)
    c = c
      .split('')
      .map((x) => x + x)
      .join('');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // Consider near-white if all channels are above 240
  return r > 240 && g > 240 && b > 240;
}

/**
 * Prettifies a field name for display
 * @param field - Field name in camelCase or schema.org format
 * @returns Human-readable field name
 */
export function prettifyField(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([a-z])([0-9])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
}

